/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Alert, Button, ProgressBar } from 'react-bootstrap';
import axios from 'axios';

const ProfileCompletionEnforcement = () => {
  const [profileStatus, setProfileStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkProfileStatus();
  }, []);

  const checkProfileStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get('/auth/profile-status', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProfileStatus(response.data);
      
      // If all requirements are met, redirect to dashboard
      if (response.data.canAcceptJobs) {
        navigate('/dashboard');
        return;
      }
    } catch (error) {
      console.error('Error checking profile status:', error);
      if (error.response?.status === 401) {
        navigate('/login');
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!profileStatus) return 0;
    
    let completed = 0;
    if (profileStatus.profileCompleted) completed++;
    if (profileStatus.licensingSubmitted) completed++;
    if (profileStatus.licensingApproved) completed++;
    
    return (completed / 3) * 100;
  };

  const getNextStep = () => {
    if (!profileStatus) return null;
    
    if (!profileStatus.profileCompleted) {
      return {
        title: 'Complete Your Profile',
        description: 'Please provide your basic information and contact details.',
        action: 'Update Profile',
        route: '/dashboard/update-account'
      };
    }
    
    if (!profileStatus.licensingSubmitted) {
      return {
        title: 'Submit Licensing Information',
        description: 'Please provide your licensing details for verification.',
        action: 'Submit Licensing',
        route: '/dashboard/update-account#licensing'
      };
    }
    
    if (!profileStatus.licensingApproved) {
      return {
        title: 'Licensing Under Review',
        description: 'Your licensing information has been submitted and is being reviewed by our admin team.',
        action: 'Check Status',
        route: '/dashboard/update-account#licensing'
      };
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Checking your account status...</p>
        </div>
      </div>
    );
  }

  const nextStep = getNextStep();
  const progress = calculateProgress();

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <Card className="shadow">
            <Card.Header className="bg-warning">
              <h3 className="mb-0 text-center">Account Setup Required</h3>
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-4">
                <h5>Welcome to Taxi-Local!</h5>
                <p className="text-muted">
                  To access job leads and start accepting bookings, please complete the following steps:
                </p>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span>Account Setup Progress</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <ProgressBar 
                  now={progress} 
                  variant={progress === 100 ? 'success' : 'warning'}
                  className="mb-3"
                />
              </div>

              {/* Requirements List */}
              <div className="mb-4">
                <h6>Requirements:</h6>
                <ul className="list-unstyled">
                  <li className={`mb-2 ${profileStatus?.profileCompleted ? 'text-success' : 'text-muted'}`}>
                    <i className={`bi ${profileStatus?.profileCompleted ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
                    {' '}Complete Profile Information
                  </li>
                  <li className={`mb-2 ${profileStatus?.licensingSubmitted ? 'text-success' : 'text-muted'}`}>
                    <i className={`bi ${profileStatus?.licensingSubmitted ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
                    {' '}Submit Licensing Information
                  </li>
                  <li className={`mb-2 ${profileStatus?.licensingApproved ? 'text-success' : 'text-muted'}`}>
                    <i className={`bi ${profileStatus?.licensingApproved ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
                    {' '}Licensing Approved by Admin
                  </li>
                </ul>
              </div>

              {/* Next Step */}
              {nextStep && (
                <Alert variant="info">
                  <h6>{nextStep.title}</h6>
                  <p className="mb-3">{nextStep.description}</p>
                  <Button 
                    variant="primary" 
                    onClick={() => navigate(nextStep.route)}
                  >
                    {nextStep.action}
                  </Button>
                </Alert>
              )}

              {/* Licensing Review Notice */}
              {profileStatus?.licensingSubmitted && !profileStatus?.licensingApproved && (
                <Alert variant="warning">
                  <h6>Licensing Under Review</h6>
                  <p className="mb-2">
                    Your licensing information has been submitted and is being reviewed by our admin team. 
                    This process typically takes 1-2 business days.
                  </p>
                  <p className="mb-0 small">
                    You will receive an email notification once your licensing has been approved.
                  </p>
                </Alert>
              )}

              {/* Help Section */}
              <div className="mt-4 p-3 bg-light rounded">
                <h6>Need Help?</h6>
                <p className="mb-2 small">
                  If you have any questions about the setup process or licensing requirements, 
                  please contact our support team.
                </p>
                <p className="mb-0 small">
                  Email: <a href="mailto:info@taxi-local.com">info@taxi-local.com</a>
                </p>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionEnforcement; 